import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import POSRetail from "@/pages/POSRetail";
import Restaurant from "@/pages/Restaurant";
import Catalog from "@/pages/Catalog";
import History from "@/pages/History";
import Reports from "@/pages/Reports";
import SettingsPage from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Shifts from "@/pages/Shifts";
import Delivery from "@/pages/Delivery";
import Customers from "@/pages/Customers";
import MobileRestaurant from "@/pages/MobileRestaurant";
import PublicMenu from "@/pages/PublicMenu";
import SupervisorLogin from "@/pages/SupervisorLogin";
import SupervisorDashboard from "@/pages/SupervisorDashboard";
import SupervisorTenantAdmin from "@/pages/SupervisorTenantAdmin";
import { AuthProvider, useAuth, usePermissions } from "@/lib/AuthContext";
import { NavGuardProvider } from "@/lib/NavGuardContext";
import { KeyboardProvider } from "@/lib/KeyboardContext";
import LoginScreen from "@/components/LoginScreen";
import DeviceGate from "@/components/DeviceGate";
import FeedbackOverlay from "@/components/FeedbackOverlay";
import GlobalUserSideEffects from "@/components/GlobalUserSideEffects";
import PostLoginShiftPrompt from "@/components/PostLoginShiftPrompt";
import ConfirmDialogHost from "@/components/ConfirmDialogHost";
import { installToastBridge } from "@/lib/toastBridge";
// Side-effect import: starts the Companion print queue poller if the
// per-device toggle is enabled (localStorage). Safe no-op otherwise.
import "@/lib/companionQueue";

// Hook sonner.toast.{success,error} → big yes/no overlay. Idempotent.
installToastBridge();

/**
 * Supervisor-only route guard. Redirects to /supervisor/login when no
 * supervisor token is present in localStorage. Keeps the supervisor
 * panel completely separate from the customer app — different JWT,
 * different localStorage namespace.
 */
function SupervisorGuard({ children }) {
  const tok = (() => {
    try { return localStorage.getItem("supervisor_token"); } catch { return null; }
  })();
  if (!tok) return <Navigate to="/supervisor/login" replace />;
  return children;
}

/**
 * Route guard — redirects to a permitted page when the user lacks ``perm``.
 *
 * For the kiosk-POS use case we DO NOT show an "Access Denied" page — we
 * silently bounce to the first available landing route so the operator
 * keeps a usable session.
 */
function Guard({ perm, children }) {
  const { can } = usePermissions();
  if (can(perm)) return children;
  return <Navigate to="/" replace />;
}

/**
 * Picks the first allowed landing route. Order = highest-value to lowest.
 */
function defaultLandingFor(can) {
  if (can("module_restaurant")) return "/restaurant";
  if (can("module_retail")) return "/pos";
  if (can("module_delivery")) return "/delivery";
  if (can("reports_cash_register") || can("reports_history") || can("reports_statistics")) return "/reports";
  if (can("view_admin")) return "/admin";
  if (can("view_settings")) return "/settings";
  return "/shifts";
}

function AuthedApp() {
  const { user, loading } = useAuth();
  const { can } = usePermissions();
  if (loading) {
    return (
      <div className="h-screen w-screen grid place-items-center bg-slate-50">
        <div className="text-slate-400 text-sm">Φόρτωση...</div>
      </div>
    );
  }
  if (!user) return <DeviceGate><LoginScreen /></DeviceGate>;
  // Waitstaff (role=waiter, label «Σερβιτόρος») get a touch-first
  // mobile layout — no sidebar, no desktop Restaurant page.
  if (user.role === "waiter") {
    return (
      <>
        <GlobalUserSideEffects />
        <PostLoginShiftPrompt />
        <ConfirmDialogHost />
        <Routes>
          <Route path="*" element={<MobileRestaurant />} />
        </Routes>
      </>
    );
  }
  const landing = defaultLandingFor(can);
  return (
    <>
      <GlobalUserSideEffects />
      <PostLoginShiftPrompt />
      <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Navigate to={landing} replace />} />
        <Route path="/pos" element={<Guard perm="module_retail"><POSRetail /></Guard>} />
        <Route path="/restaurant" element={<Guard perm="module_restaurant"><Restaurant /></Guard>} />
        <Route path="/delivery" element={<Guard perm="module_delivery"><Delivery /></Guard>} />
        <Route path="/catalog" element={<Guard perm="view_items"><Catalog /></Guard>} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/history" element={<Guard perm="reports_history"><History /></Guard>} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/settings" element={<Guard perm="view_settings"><SettingsPage /></Guard>} />
        <Route path="/admin" element={<Guard perm="view_admin"><Admin /></Guard>} />
        <Route path="/shifts" element={<Shifts />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        richColors
        toastOptions={{ classNames: { toast: "font-sans" } }}
      />
      {/* Full-screen yes/no feedback shown for toast.success / toast.error
          across the whole app (desktop + mobile). Mounted once at root. */}
      <FeedbackOverlay />
      <ConfirmDialogHost />
      <Routes>
        {/* Δημόσιος QR τιμοκατάλογος — εκτός auth & DeviceGate */}
        <Route path="/menu/:token" element={<PublicMenu />} />
        {/* Supervisor Panel - completely outside the customer auth flow */}
        <Route path="/supervisor/login" element={<SupervisorLogin />} />
        <Route
          path="/supervisor"
          element={
            <SupervisorGuard>
              <SupervisorDashboard />
            </SupervisorGuard>
          }
        />
        <Route
          path="/supervisor/tenants/:tenantCode/manage"
          element={
            <SupervisorGuard>
              <SupervisorTenantAdmin />
            </SupervisorGuard>
          }
        />
        {/* Customer app - everything else */}
        <Route
          path="/*"
          element={
            <AuthProvider>
              <NavGuardProvider>
                <KeyboardProvider>
                  <AuthedApp />
                </KeyboardProvider>
              </NavGuardProvider>
            </AuthProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
